import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { getAllUserProfiles, saveUserProfile } from '@shared/services/userService';
import { enableUserProfile, deactivateUserProfile, deleteUserProfile } from '../services/registrationService';
import { UserProfile } from '../types';
import {
    CheckCircleIcon,
    XIcon,
    UsersIcon,
    ShieldCheckIcon,
    BotIcon,
    SparklesIcon,
    SearchIcon,
    InfoIcon,
    AlertCircleIcon,
    ArrowLeftIcon,
    SlidersHorizontalIcon,
    SaveIcon,
    LayoutGridIcon,
    PlusIcon,
    TrashIcon,
    PencilIcon,
    RefreshCwIcon,
    ImagePlusIcon
} from '../components/icons';
import { homeService } from '../services/homeService';
import { CarouselItem } from '../data/carouselData';
import { HomepageHeroImage } from '../data/homepageImages';
import CarouselManagementTab from '../components/CarouselManagementTab';
import PortfolioManagementTab from '../components/PortfolioManagementTab';

import { SYSTEM_AGENTS } from '../constants';
import { BACKEND_CONFIG, FIREBASE_CONFIG } from '../config';
import {
    isSystemAgentActive,
    toggleSystemAgent,
    toggleUserAgent
} from '../services/agentService';
import { portfolioService } from '../services/portfolioService';
import { PortfolioItem, PricingTier } from '@shared/types';
import Spinner from '../components/Spinner';

const AdminDashboardPage: React.FC = () => {
    const { t } = useTranslation();
    const [profiles, setProfiles] = useState<{ userId: string, profile: UserProfile }[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'users' | 'agents' | 'settings' | 'carousel' | 'portfolio'>('users');
    const [searchTerm, setSearchTerm] = useState('');
    const [agentSearchTerm, setAgentSearchTerm] = useState('');

    useEffect(() => {
        loadProfiles();
    }, []);



    const loadProfiles = () => {
        setLoading(true);
        // Simulate fetch delay
        setTimeout(() => {
            const allProfiles = getAllUserProfiles();
            // Filter out guest and admin-user for management
            setProfiles(allProfiles.filter(p => !p.userId.includes('guest') && p.userId !== 'admin-user'));
            setLoading(false);
        }, 800);
    };

    const handleEnableProfile = (userId: string, profile: UserProfile) => {
        const updated = enableUserProfile(profile);
        saveUserProfile(userId, updated);
        loadProfiles();
    };

    const handleDisableProfile = (userId: string, profile: UserProfile) => {
        const updated = deactivateUserProfile(profile);
        saveUserProfile(userId, updated);
        loadProfiles();
    };

    const handleDeleteProfile = (userId: string, profile: UserProfile) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            const updated = deleteUserProfile(profile);
            saveUserProfile(userId, updated);
            loadProfiles();
        }
    };

    const renderCarouselTab = () => (
        <CarouselManagementTab />
    );


    const handleToggleSystemAgent = (agentId: string) => {
        toggleSystemAgent(agentId);
        // Trigger re-render by refreshing profiles (even though system agents aren't in profiles, 
        // they are global and we need to refresh the view)
        loadProfiles();
    };

    const handleToggleUserAgent = (userId: string, agentId: string) => {
        toggleUserAgent(userId, agentId);
        loadProfiles();
    };

    const filteredProfiles = profiles.filter(({ userId, profile }) => {
        const search = searchTerm.toLowerCase();
        const email = profile.email || '';
        const displayName = profile.displayName || '';
        return (
            userId.toLowerCase().includes(search) ||
            email.toLowerCase().includes(search) ||
            displayName.toLowerCase().includes(search)
        );
    });

    const filteredAgents = profiles.flatMap(p =>
        (p.profile.styleAgents || []).map(a => ({ ...a, userId: p.userId, userName: p.profile.displayName || p.userId }))
    ).filter(a => {
        const name = a.name || '';
        const userName = a.userName || '';
        return (
            name.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
            userName.toLowerCase().includes(agentSearchTerm.toLowerCase())
        );
    });

    const renderUsersTab = () => (
        <motion.div
            key="users"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
        >
            <div className="mb-8 relative">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder={t('adminDashboard_searchPlaceholder') || 'Search users by email or ID...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl py-4 pl-12 pr-4 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600/20 transition-all shadow-sm"
                />
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <Spinner className="w-12 h-12 text-indigo-600" />
                </div>
            ) : filteredProfiles.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <UsersIcon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">{t('adminDashboard_noProfiles')}</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 dark:bg-gray-900/50">
                            <tr>
                                <th className="px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">{t('adminDashboard_profile')}</th>
                                <th className="px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">Level</th>
                                <th className="px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">{t('adminDashboard_status')}</th>
                                <th className="px-6 py-4 text-sm font-bold text-gray-500 uppercase tracking-wider">{t('adminDashboard_actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {filteredProfiles.map(({ userId, profile }) => (
                                <tr key={userId} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                                <UsersIcon className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-gray-900 dark:text-white truncate max-w-[200px]">{profile.email || userId}</p>
                                                <p className="text-xs text-gray-500">ID: {userId.slice(0, 12)}...</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                                            {profile.membershipLevel || 'basic'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${profile.accountStatus === 'active'
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : profile.accountStatus === 'validated' || profile.accountStatus === 'activated'
                                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                : profile.accountStatus === 'inactive'
                                                    ? 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                                                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                            {profile.accountStatus === 'active'
                                                ? 'Active'
                                                : profile.accountStatus === 'validated' || profile.accountStatus === 'activated'
                                                    ? 'Activated'
                                                    : profile.accountStatus || 'Unknown'
                                            }
                                        </span>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-2">
                                            {profile.accountStatus !== 'active' ? (
                                                <button
                                                    onClick={() => handleEnableProfile(userId, profile)}
                                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 px-4 rounded-lg transition-all shadow-sm"
                                                >
                                                    <ShieldCheckIcon className="w-4 h-4" />
                                                    {t('adminDashboard_enable')}
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleDisableProfile(userId, profile)}
                                                    className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold py-2 px-4 rounded-lg transition-all"
                                                >
                                                    <XIcon className="w-4 h-4" />
                                                    {t('adminDashboard_disable')}
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </motion.div>
    );

    const renderAgentsTab = () => (
        <motion.div
            key="agents"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
        >
            <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                    <SparklesIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    <h2 className="text-2xl font-bold dark:text-white">System Agents</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {SYSTEM_AGENTS.map(agent => (
                        <div key={agent.id} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all">
                            <div className="flex justify-between items-start mb-4">
                                <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                                    <BotIcon className="w-8 h-8" />
                                </div>
                                <button
                                    onClick={() => handleToggleSystemAgent(agent.id)}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${isSystemAgentActive(agent.id)
                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                        }`}
                                >
                                    {isSystemAgentActive(agent.id) ? 'Active' : 'Inactive'}
                                </button>
                            </div>
                            <h3 className="text-xl font-bold mb-2 dark:text-white">{t(agent.nameKey)}</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mb-4 line-clamp-2">{t(agent.descriptionKey)}</p>
                            <div className="flex items-center gap-2 pt-4 border-t border-gray-50 dark:border-gray-700">
                                <ShieldCheckIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-[10px] uppercase font-bold text-gray-400 tracking-tighter">Requires {agent.requiredMembership || 'free'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-end gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <UsersIcon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                        <h2 className="text-2xl font-bold dark:text-white">User Created Agents</h2>
                    </div>
                    <div className="relative w-full md:w-64">
                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search agents or owners..."
                            value={agentSearchTerm}
                            onChange={(e) => setAgentSearchTerm(e.target.value)}
                            className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600/20"
                        />
                    </div>
                </div>

                {filteredAgents.length === 0 ? (
                    <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl border border-dashed border-gray-200 dark:border-gray-700">
                        <BotIcon className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                        <p className="text-gray-500">No user agents found</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredAgents.map((agent: any) => (
                            <div key={`${agent.userId}-${agent.id}`} className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                        <SparklesIcon className="w-8 h-8" />
                                    </div>
                                    <button
                                        onClick={() => handleToggleUserAgent(agent.userId, agent.id)}
                                        className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${agent.isActive
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}
                                    >
                                        {agent.isActive ? 'Active' : 'Inactive'}
                                    </button>
                                </div>
                                <h3 className="text-xl font-bold mb-1 dark:text-white capitalize">{agent.name}</h3>
                                <p className="text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-widest mb-4">By {agent.userName}</p>

                                <div className="grid grid-cols-2 gap-2 mb-4">
                                    <div className="p-2 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                        <p className="text-[8px] uppercase text-gray-400 font-bold mb-1">Frequency</p>
                                        <p className="text-xs font-bold dark:text-white truncate">{agent.frequency}</p>
                                    </div>
                                    <div className="p-2 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                        <p className="text-[8px] uppercase text-gray-400 font-bold mb-1">Focus</p>
                                        <p className="text-xs font-bold dark:text-white truncate">{agent.focusArea}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-4 border-t border-gray-50 dark:border-gray-700">
                                    <div className="flex items-center gap-1.5">
                                        <InfoIcon className="w-3 h-3 text-gray-400" />
                                        <span className="text-[8px] uppercase font-bold text-gray-400 tracking-tighter">ID: {agent.id.slice(0, 8)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </motion.div>
    );

    const renderPortfolioTab = () => (
        <PortfolioManagementTab />
    );

    const renderSettingsTab = () => (
        <motion.div
            key="settings"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
        >
            <div className="max-w-2xl mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                            <ShieldCheckIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold dark:text-white">Backend Configuration</h2>
                            <p className="text-sm text-gray-500">Configure how the application connects to the Agentic Infrastructure.</p>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl">
                            <div>
                                <p className="font-bold dark:text-white">Use Remote Backend</p>
                                <p className="text-xs text-gray-500">Connect to the real backend API instead of using local fallback.</p>
                            </div>
                            <button
                                onClick={() => {
                                    const newValue = !BACKEND_CONFIG.USE_EXTERNAL_BACKEND;
                                    localStorage.setItem('VITE_USE_EXTERNAL_BACKEND', newValue ? 'true' : 'false');
                                    window.location.reload();
                                }}
                                aria-label="Toggle Remote Backend"
                                className={`w-14 h-8 rounded-full transition-all relative ${BACKEND_CONFIG.USE_EXTERNAL_BACKEND ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`}
                            >
                                <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${BACKEND_CONFIG.USE_EXTERNAL_BACKEND ? 'left-7' : 'left-1'}`} />
                            </button>
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-2xl">
                            <div>
                                <p className="font-bold dark:text-white">Use Firestore Persistence</p>
                                <p className="text-xs text-gray-500">Persist Carousel and Portfolio data directly to Firestore.</p>
                            </div>
                            <button
                                onClick={() => {
                                    const newValue = !FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE;
                                    localStorage.setItem('VITE_USE_FIREBASE_CONFIG_STORAGE', newValue ? 'true' : 'false');
                                    window.location.reload();
                                }}
                                aria-label="Toggle Firestore Persistence"
                                className={`w-14 h-8 rounded-full transition-all relative ${FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-700'}`}
                            >
                                <div className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all ${FIREBASE_CONFIG.USE_FIREBASE_CONFIG_STORAGE ? 'left-7' : 'left-1'}`} />
                            </button>
                        </div>

                        <div className="space-y-2">
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 ml-1">
                                Backend API URL
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    defaultValue={BACKEND_CONFIG.BACKEND_URL}
                                    id="backend_url_input"
                                    className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600/20"
                                    placeholder="http://localhost:3000"
                                    title="Backend API URL"
                                />
                                <button
                                    onClick={() => {
                                        const el = document.getElementById('backend_url_input') as HTMLInputElement;
                                        localStorage.setItem('VITE_BACKEND_URL', el.value);
                                        window.location.reload();
                                    }}
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold text-sm transition-all shadow-lg flex items-center gap-2"
                                >
                                    <SaveIcon className="w-4 h-4" />
                                    Save
                                </button>
                            </div>
                            <p className="text-[10px] text-gray-500 ml-1 italic">
                                Default: {typeof import.meta !== 'undefined' ? ((import.meta as any).env?.VITE_BACKEND_URL || 'http://localhost:3000') : 'http://localhost:3000'}
                            </p>
                        </div>

                        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-2xl flex gap-3">
                            <AlertCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                            <p className="text-xs text-amber-700 dark:text-amber-300 leading-relaxed">
                                Changing these settings will cause a page reload. Ensure you have saved any unsaved work before applying changes.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24 pb-12 px-6">
            <div className="max-w-6xl mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl font-serif font-bold text-gray-900 dark:text-white mb-2">
                        {t('adminDashboard_title')}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {t('adminDashboard_subtitle')}
                    </p>
                </div>

                <div className="flex flex-wrap bg-white dark:bg-gray-800 p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm w-fit mb-8 gap-1">
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all ${activeTab === 'users'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <UsersIcon className="w-5 h-5" />
                        {t('adminDashboard_tabUsers') || 'Users'}
                    </button>
                    <button
                        onClick={() => setActiveTab('agents')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all ${activeTab === 'agents'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <BotIcon className="w-5 h-5" />
                        {t('adminDashboard_tabAgents') || 'Agents'}
                    </button>
                    <button
                        onClick={() => setActiveTab('settings')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all ${activeTab === 'settings'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <SlidersHorizontalIcon className="w-5 h-5" />
                        {t('adminDashboard_tabSettings') || 'Settings'}
                    </button>
                    <button
                        onClick={() => setActiveTab('carousel')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all ${activeTab === 'carousel'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <LayoutGridIcon className="w-5 h-5" />
                        {t('adminDashboard_tabCarousel') || 'Home Data'}
                    </button>
                    <button
                        onClick={() => setActiveTab('portfolio')}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-all ${activeTab === 'portfolio'
                            ? 'bg-indigo-600 text-white shadow-lg'
                            : 'text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                    >
                        <SlidersHorizontalIcon className="w-5 h-5" />
                        {t('adminDashboard_tabPortfolio') || 'Portfolio Data'}
                    </button>
                </div>


                <AnimatePresence mode="wait">
                    {activeTab === 'users' ? (
                        renderUsersTab()
                    ) : activeTab === 'agents' ? (
                        renderAgentsTab()
                    ) : activeTab === 'carousel' ? (
                        renderCarouselTab()
                    ) : activeTab === 'portfolio' ? (
                        renderPortfolioTab()
                    ) : (
                        renderSettingsTab()
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default AdminDashboardPage;
